/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    object, string, array, func, shape, bool, arrayOf, number,
} from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import ReactHtmlParser from 'react-html-parser';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Grid } from '@material-ui/core';

// helpers, states
import { getFeatureFlags } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import DesktopMobile from '../../../../../helpers/DesktopMobile/DesktopMobile';
import { setUserSubmittedProductFilterZipcode } from '../../../../../../state/ducks/App/App-Actions';
import { actions as tagManagerActions } from '../../../../../../state/ducks/TagManager/ducks/TagManager';

// UI Components
import WeatherConditions from './WeatherConditions';
import ConnectedGraphqlPlantsProductSkus from './GraphqlPlantsProductSkus';
import ProductGiftMessage from './ProductGiftMessage';
import ProductInfoDivider from './ProductInfoDivider';
import ShippingInformation from './ShippingInformation';
import ProductVaseSelector from './ProductVaseSelector';
import ProductQuantityUI from '../../Partials/ProductQuantity';
import ConnectedGraphqlPlantsAddToCart from './GraphqlPlantsAddToCart/GraphqlPlantsAddToCart';
import { clubComponentType, addToCartDataType } from '../../Partials/common/types';
import { extractVaseCode, weatherArray } from '../ProductPageFunctions';
import SingleVariantPrice from './SingleVariantPrice';
import ProductStateSection from './ProductStateSection';
import states from './StateList.json';
import ProductUnableToShip from './ProductUnableToShip';

const styles = (theme) => ({
    cartWrapper: {
        [theme.breakpoints.down('xs')]: {
            display: 'grid',
        },
    },
    passportBanner: {
        display: 'inline-block',
        width: '60%',
        float: 'left',
        // Mobile
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
        [theme.breakpoints.down('xs')]: {
            'grid-row': 2,
            marginTop: 20,
        },
    },
    addToCart: {
        display: 'inline-block',
        float: 'right',
        // Mobile
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    addToCartFullWidth: {
        width: '100%!important',
    },
    productName: {
        width: '100%',
        marginTop: 0,
        fontSize: '2.5rem',
        fontFamily: theme.typography.secondaryFontFamily,
        lineHeight: '40px',
        marginBottom: 0,
        fontWeight: 400,
        [theme.breakpoints.down('md')]: {
            paddingTop: 10,
            display: 'block',
            fontSize: '1.5rem',
        },
    },
    zipCodeInput: {
        width: '100%',
        display: 'block',  // IE 11
        '& > div': {
            width: '100%', // IE 11
        },
    },
    zipCodeInputValid: {
        '&::after': {
            position: 'absolute',
            zIndex: '999',
            // margin: '25px 0px 0px 50px',
            backgroundColor: theme.palette.colorValid || '#24b200',
            color: theme.palette.white,
            textAlign: 'center',
            content: '"✓"',
            margin: '-60px 0px 0px 95px',
            fontSize: '18px',
            padding: '5px',
            fontWeight: 'bold',
            lineHeight: '17px',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            animation: 'fadeZoomIn 0.3s ease-in',
            display: 'inline-block', // IE 11
        },
    },
    zipCodeInputInvalidIcon: {
        '&::after': {
            position: 'absolute',
            zIndex: '999',
            backgroundColor: theme.palette.cms?.errorZipVerifyPDP || '#79000f',
            color: theme.palette.white,
            textAlign: 'center',
            content: '"X"',
            fontSize: '17px',
            padding: '5px',
            fontWeight: 'bold',
            margin: '-32px 0px 0px 96px',
            lineHeight: '17px',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            animation: 'fadeZoomIn 0.3s ease-in',
            display: 'inline-block', // IE 11
        },
    },
    zipInputTextValid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.colorValid || '#24b200',
            lineHeight: '25px',
            fontSize: '14px',
            fontWeight: '400',
            content: '"ZIP code confirmed eligible "',
            margin: '10px 0px 0px -100px',
            border: 'none',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    zipCodeInputInvalid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.cms?.errorZipVerifyPDP || '#79000f',
            lineHeight: '25px',
            fontSize: '14px',
            fontWeight: '400',
            content: '"Not eligible for Wine shipment"',
            bottom: '-34px',
            left: 0,
            paddingLeft: '36px',
            width: 'calc(100% - 8px)',
            height: '25px',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    ageInput: {
        width: '100%',
        display: 'flex',  // IE 11
        padding: '8px 0px 8px 0px',
        '& > div': {
            width: '100%', // IE 11
        },
    },
    ageInputInvalid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.cms?.errorZipVerifyPDP || '#79000f',
            lineHeight: '25px',
            fontSize: '12px',
            border: 'none',
            fontWeight: '400',
            content: '"You must be 21 or older to purchase Wine !"',
            height: '22px',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    dateInvalid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.cms?.errorZipVerifyPDP || '#79000f',
            lineHeight: '25px',
            fontSize: '12px',
            border: 'none',
            fontWeight: '400',
            margin: '0px 0px 0px 20px',
            content: '"Please enter a valid date of birth !"',
            height: '22px',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    ageInputInvalidIcon: {
        '&::after': {
            position: 'absolute',
            zIndex: '999',
            margin: '-50px 0px 0px 205px',
            backgroundColor: theme.palette.cms?.errorZipVerifyPDP || '#79000f',
            color: theme.palette.white,
            textAlign: 'center',
            content: '"x"',
            fontSize: '20px',
            padding: '3px',
            fontWeight: 'bold',
            lineHeight: '17px',
            left: '7px',
            width: '25px',
            height: '25px',
            borderRadius: '50%',
            animation: 'fadeZoomIn 0.3s ease-in',
            display: 'inline-block', // IE 11
        },
    },
    grid: {
        width: '250px',
        height: '90px',
    },
    ageInputTextValid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.colorValid || '#24b200',
            lineHeight: '25px',
            fontSize: '14px',
            fontWeight: '400',
            content: '"Age Confirmed"',
            width: '95px',
            margin: '0px 0px 0px 12px',
            border: 'none',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    ageInputValid: {
        '&::after': {
            position: 'absolute',
            zIndex: '999',
            backgroundColor: theme.palette.colorValid || '#24b200',
            color: theme.palette.white,
            textAlign: 'center',
            content: '"✓"',
            margin: '-50px 0px 0px 215px',
            fontSize: '20px',
            padding: '7px',
            fontWeight: 'bold',
            lineHeight: '17px',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            animation: 'fadeZoomIn 0.3s ease-in',
            display: 'inline-block', // IE 11
        },
    },
    addToCartWrapper: {
        display: 'flex',
        alignItems: 'center',
        '& select, & input': {
            background: '#F3F3F3',
            maxWidth: 65,
            textAlign: 'center',
        },
        '& p': {
            margin: '0px !important',
            fontSize: '1rem',
        },
        '& div': {
            border: 'none',
            '&:before': {
                border: 'none',
            },
        },
        '& button': {
            color: '#000',
        },
    },
    goDeluxeContainer: {
        // Mobile
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
    fromValue: {
        fontSize: '16px',
        color: theme.palette.pdp?.priceFromValue || '#585858',
        marginTop: 0,
        [theme.breakpoints.down('sm')]: {
            padding: '0 20px',
        },
    },
    shortDescription: {
        marginTop: '10px',
        lineHeight: '24px',
        letterSpacing: '.2px',
    },
    productGiftMessageContainer: {
        paddingLeft: '11px !important',
    },
});

const ShortDescription = ({ classes, shortDescription }) => (
    <Grid container className={classes.shortDescription}>
        {ReactHtmlParser(shortDescription)}
    </Grid>
);

ShortDescription.propTypes = {
    classes: object.isRequired,
    shortDescription: string.isRequired,
};

const GraphqlPlantsPDPDataWrapper = ({
    classes,
    product,
    product: {
        productSkus,
        brandId,
        partNumber,
        name,
        displayDeliveryCalendar,
    },
    skuConfig,
    selectedSku,
    handleSkuChange,
    sortedData,
    presentation_family,
    productUrl,
    subscriptionDetails,
    subscription,
    location,
    brand,
    skuMonths,
    changeClubMonth,
    changeClubLength,
    clubUpsells,
    selectedClubUpsells,
    changeClubUpsell,
    disableAddToCart,
    setDisableAddToCart,
    personalizedData,
    isAddToCartEnabled,
    hasWine,
    selectedAddons,
    quantity,
    setProductQuantity,
    baseProductSku,
    isPassportBundleProduct,
    clubProduct,
    selectedState,
    setSelectedState,
    setAltertType,
    alertType,
    addToCartClicked,
    handleMobileAgeVerification,
    handleMiniCartModalClick,
    setAddToCartClicked,
    deliveryDate,
    setDeliveryDate,
    variantVaseList,
    renderVase,
    commonStates,
    isVaseTemplate,
    isSmallScreen,
    content,
    setToastError,
    vaseList,
    shortDescription,
}) => {
    if (!product) {
        return null;
    }
    const isMobile = !useMediaQuery('(min-width:600px)');
    const [sizeChartOpen, setSizeChartOpen] = useState(false);

    const stateEnabled = content?.entries && content?.entries[0]?.show_state;

    const sizeChartToggle = () => setSizeChartOpen(!sizeChartOpen);

    const featureFlags = useSelector(getFeatureFlags);

    const dispatch = useDispatch();

    let restrictedStates = null;
    const additionalInformation = content?.entries && content?.entries[0]?.additional_information && content?.entries[0]?.additional_information;

    if (additionalInformation?.length > 0) {
        additionalInformation.forEach((item) => {
            if (item['restrict_state']) {
                restrictedStates = item.restrict_state;
                return restrictedStates;
            }
            return null;
        });
    }

    const handleStateChange = (e) => {
        if (!e.target.value) return null;
        dispatch(tagManagerActions.trackEvent({
            eventName: 'feature_interaction', // Requird for GA4 feature_click
            eventCategory: 'Product Page',
            eventAction: 'Change',
            eventLabel: e.target.value,
        }));
        setSelectedState(e.target.value);
        const zipCode = states.find((item) => item.code === e.target.value);
        dispatch(setUserSubmittedProductFilterZipcode(zipCode.default_zip_code));
        const isRestrictedState = restrictedStates?.restricted_state.includes(e.target.value);

        if (e.target.value === 'AK' || e.target.value === 'HI') {
            setAltertType('nonDeliverable');
            // There's no need to disable the button for Hawai and Alaska
            setDisableAddToCart(false);
            setToastError(true);
        } else if (isRestrictedState) {
            setAltertType('nonDeliverableOther');
            setDisableAddToCart(true);
            setToastError(true);
        } else {
            setAltertType('');
            setDisableAddToCart(false);
            setToastError(false);
        }
        return e.target.value;
    };

    const graphqlPlantsProductSkusProps = {
        skuData: sortedData,
        config: skuConfig,
        handleSkuChange,
        selectedSku,
        presentation_family,
        product,
        productUrl,
        location,
        brand,
        skuMonths,
        clubUpsells,
        changeClubMonth,
        changeClubLength,
        selectedClubUpsells,
        changeClubUpsell,
        handleMiniCartModalClick,
        baseProductSku,
        isPassportBundleProduct: isPassportBundleProduct || {},
        clubProduct,
        isPdpYotpoEnabled: featureFlags['is-pdp-yotpo-enabled'],
    };
    const sizeChartValues = content?.entries?.[0]?.additional_information?.[1]?.size_chart?.size_chart;
    const weatherConditionProps = {
        weatherData: weatherArray(selectedSku),
        sizeChartToggle,
        variantSoldOut: false,
        mobileVIew: false,
        showWaitListBtnControl: false,
        sizeChartValues,
    };

    const productQuantityProps = {
        quantity,
        setProductQuantity,
        classes,
        label: 'Qty',
    };

    const productStateSectionProps = {
        selectedState,
        handleStateChange,
        setDisableAddToCart,
        setSelectedState,
        setAltertType,
        alertType,
        addToCartClicked,
    };

    const productGiftMessageProps = {
        product,
        isPassportBundleProduct,
        selectedSku,
        hasWine,
        deliveryDate,
        setDeliveryDate,
        selectedState,
        stateEnabled,
    };

    const addToCartProps = {
        isAddToCartEnabled,
        selectedItemId: selectedSku.id,
        brandId,
        productSkus,
        partNumber,
        categoryId: location.state?.categoryId || '',
        categoryPath: location.state?.categoryPath || '',
        categoryName: location.state?.categoryName || '',
        subscription,
        subscriptionDetails,
        fullWidth: true,
        disableAddToCart,
        setDisableAddToCart,
        personalizedData,
        isPersonalizable: product?.isPersonalizable,
        product,
        presentation_family,
        handleMiniCartModalClick,
        quantity,
        handleMobileAgeVerification,
        isMobile,
        hasWine,
        isPassportBundleProduct: isPassportBundleProduct || {},
        productType: selectedSku?.productType,
        addons: selectedAddons,
        setAddToCartClicked,
        selectedState,
        displayDeliveryCalendar,
        deliveryDate,
        stateEnabled,
        location,
        alertType,
    };

    const uniqueVariantSize = isVaseTemplate && Array.from(
        new Map(product?.productSkus?.map((variantSize) => [variantSize?.id[11], variantSize]))
            .values())
        .filter((variantSize) => extractVaseCode(variantSize?.id));

    const productVaseSelectorProps = {
        variantList: product.productSkus,
        toggleVase: () => {},
        getVase: renderVase,
        variantLength: product?.productSkus?.length,
        state: {
            variantVaseList: product?.productSkus,
            vaseList: [],
            isSmallScreen,
            activeIndex: 0,
        },
        classes,
        getBrowserName: {},
        isFromQuickView: false,
        ENUMS: {},
        variantVaseList,
        commonStates,
        isSmallScreen,
        vaseList,
        uniqueVariantSize,
    };

    const variantLength = product?.productSkus?.length > 1;
    /* eslint-disable react/jsx-props-no-spreading */
    return (
        <>
            <DesktopMobile
                breakpoint={768}
                desktop={() => (
                    <>
                        <h1 className={classes.productName}>
                            {ReactHtmlParser(name)}
                            {(!variantLength || isVaseTemplate) && (<SingleVariantPrice sku={isVaseTemplate ? selectedSku : product.productSkus[0]} />)}
                        </h1>
                    </>
                )}
                mobile={() => (
                    <>
                        {(isVaseTemplate && uniqueVariantSize?.length) ? (
                            <>
                                <SingleVariantPrice className={classes.productName} sku={selectedSku} />
                                <ProductInfoDivider />
                                {shortDescription && (
                                    <>
                                        <ShortDescription classes={classes} shortDescription={shortDescription} />
                                        <ProductInfoDivider />
                                    </>
                                )}
                                <ProductVaseSelector {...productVaseSelectorProps} />
                            </>
                        ) : (
                            (variantLength && (
                                <>
                                    <ConnectedGraphqlPlantsProductSkus {...graphqlPlantsProductSkusProps} />
                                    {shortDescription && (
                                        <>
                                            <ProductInfoDivider />
                                            <ShortDescription classes={classes} shortDescription={shortDescription} />
                                        </>
                                    )}
                                    <ProductInfoDivider />
                                </>
                            )) || (
                                <>
                                    <SingleVariantPrice className={classes.productName} sku={selectedSku} />
                                    {shortDescription && (
                                        <>
                                            <ProductInfoDivider />
                                            <ShortDescription classes={classes} shortDescription={shortDescription} />
                                        </>
                                    )}
                                    <ProductInfoDivider />
                                </>
                            )
                        )}
                    </>
                )}
            />
            <DesktopMobile
                breakpoint={768}
                desktop={() => shortDescription && (
                    <>
                        <ShortDescription classes={classes} shortDescription={shortDescription} />
                        {!sizeChartValues && <ProductInfoDivider />}
                    </>
                )}
                mobile={() => (<></>)}
            />
            {sizeChartValues && <> <ProductInfoDivider /> <WeatherConditions {...weatherConditionProps} /> </>}
            <DesktopMobile
                breakpoint={768}
                desktop={() => (
                    product?.productSkus?.length > 1 ? (
                        <>
                            {(isVaseTemplate && uniqueVariantSize?.length) ? (
                                <>
                                    <ProductInfoDivider />
                                    <ProductVaseSelector {...productVaseSelectorProps} />
                                </>
                            ) : (
                                <ConnectedGraphqlPlantsProductSkus {...graphqlPlantsProductSkusProps} />
                            )}
                            <ProductInfoDivider />
                        </>
                    ) : null
                )}
                mobile={() => sizeChartValues && <ProductInfoDivider />}
            />
            {stateEnabled && <ProductStateSection {...productStateSectionProps} />}
            {(featureFlags['is-product-gift-message-enabled']) && <div className={classes.productGiftMessageContainer}><ProductGiftMessage {...productGiftMessageProps} /></div>}
            <DesktopMobile
                breakpoint={768}
                desktop={() => (<><ProductInfoDivider /><ShippingInformation isSmallScreen={isSmallScreen} /></>)}
                mobile={() => (<></>)}
            />
            <ProductInfoDivider />
            <div className={classes.addToCartWrapper}>
                <ProductQuantityUI {...productQuantityProps} />
                <ConnectedGraphqlPlantsAddToCart {...addToCartProps} />
            </div>
            {(restrictedStates && stateEnabled) && <ProductUnableToShip restrictedStates={restrictedStates} />}
        </>
    );
};

GraphqlPlantsPDPDataWrapper.propTypes = {
    classes: object.isRequired,
    skuConfig: object.isRequired,
    product: object.isRequired,
    deliveryData: object.isRequired,
    partNumber: string,
    hasWine: bool.isRequired,
    presentation_family: string.isRequired,
    selectedSku: object.isRequired,
    handleSkuChange: func.isRequired,
    sortedData: array.isRequired,
    productUrl: string.isRequired,
    subscriptionDetails: shape({
        subscriptionType: string,
        subscriptionDuration: string,
        subscriptionFrequency: string,
    }),
    subscription: shape({
        subscriptionKey: array,
        subscriptionIndefinite: string,
        subscriptionType: array,
        implementProductSubscription: bool,
        viewSubscription: bool,
        isTypeDiscovery: bool,
    }),
    triggerSubscriptionCheck: func,
    location: object.isRequired,
    brand: shape({
        domain: string,
    }),
    isPassportBundleItem: bool,
    skuMonths: object, // dynamic month named keys
    changeClubMonth: func,
    changeClubLength: func,
    clubUpsells: clubComponentType,
    selectedClubUpsells: string,
    changeClubUpsell: func,
    displayDeliveryCalendar: bool,
    handlePersonalizeInputChange: func,
    disableAddToCart: bool,
    setDisableAddToCart: func,
    getUpdatedPersonalizedProdData: func,
    personalizedData: shape({
        personalizationType: string,
        personalizationLines: arrayOf({
            perAttributeValue: string,
        }),
    }),
    personalization: shape({
        lines: arrayOf(
            shape({
                lineNumber: number,
                maxChars: number,
            }),
        ),
    }),
    isAddToCartEnabled: bool.isRequired,
    updateWineStatus: func,
    hasAddons: bool,
    selectedAddons: arrayOf(string),
    handleAddonChange: func,
    featureFlags: object.isRequired,
    quantity: string,
    setProductQuantity: func,
    baseProductSku: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                    __typename: string.isRequired,
                }),
            ).isRequired,
            __typename: string.isRequired,
        }),
    ),
    isPassportBundleProduct: shape({
        prices: {
            value: number,
        },
        enable: bool,
    }),
    clubProduct: bool,
    mobileAgeVerificationOpen: bool,
    handleMobileAgeVerification: func,
    addToCartData: addToCartDataType,
    selectedState: string.isRequired,
    setSelectedState: func.isRequired,
    setAltertType: func.isRequired,
    alertType: string.isRequired,
    addToCartClicked: func.isRequired,
    handleMiniCartModalClick: func.isRequired,
    setAddToCartClicked: func.isRequired,
    deliveryDate: string.isRequired,
    setDeliveryDate: func.isRequired,
    variantVaseList: bool,
    renderVase: func,
    commonStates: object.isRequired,
    isVaseTemplate: bool.isRequired,
    isSmallScreen: bool.isRequired,
    content: object.isRequired,
    setToastError: func.isRequired,
    vaseList: array.isRequired,
    shortDescription: string,
};

GraphqlPlantsPDPDataWrapper.defaultProps = {
    partNumber: null,
    subscriptionDetails: {},
    subscription: {},
    clubUpsells: [],
    skuMonths: {},
    brand: {},
    isPassportBundleItem: false,
    changeClubMonth: () => { },
    changeClubLength: () => { },
    triggerSubscriptionCheck: () => { },
    selectedClubUpsells: null,
    changeClubUpsell: () => { },
    handlePersonalizeInputChange: () => { },
    disableAddToCart: false,
    setDisableAddToCart: () => { },
    getUpdatedPersonalizedProdData: () => { },
    personalizedData: {},
    personalization: {},
    updateWineStatus: () => { },
    hasAddons: false,
    displayDeliveryCalendar: true,
    selectedAddons: {},
    handleAddonChange: () => { },
    quantity: '',
    setProductQuantity: () => { },
    baseProductSku: null,
    isPassportBundleProduct: false,
    clubProduct: false,
    mobileAgeVerificationOpen: false,
    handleMobileAgeVerification: () => { },
    addToCartData: {},
    variantVaseList: {},
    renderVase: () => { },
    shortDescription: '',
};

export default withStyles(styles)(GraphqlPlantsPDPDataWrapper);
